import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface ApiKey {
  id: string;
  tenantId: string;
  userId: string;
  label: string;
  hint: string;
  scopes: string[];
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  usageCount: number;
}

export interface MintedApiKey extends ApiKey {
  /** Plaintext — shown once, never retrievable again. */
  plaintext: string;
}

export function useApiKeys() {
  const api = useApi();
  return useQuery<ApiKey[]>({
    queryKey: ['api-keys'],
    queryFn: () => api.fetch('/api-keys'),
  });
}

export function useMintApiKey() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label: string; expiresAt?: string; scopes?: string[] }) =>
      api.fetch<MintedApiKey>('/api-keys', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });
}

export function useRevokeApiKey() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.fetchRaw(`/api-keys/${id}`, { method: 'DELETE' }).then((r) => {
        if (!r.ok && r.status !== 204) throw new Error(`Revoke failed: ${r.status}`);
        return true;
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });
}
